import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Carousel, CarouselContent, CarouselItem } from "../components/ui/carousel";
import { Button } from "../components/ui/button";
import {
  CalendarDays, QrCode, Receipt, FileSignature, FileSpreadsheet, Brain,
  ChevronLeft, ChevronRight, ArrowUpRight,
} from "lucide-react";

const SLIDES = [
  {
    id: "calendar",
    kicker: "01 · Calendario Intelligente",
    title: "Agenda smart, come Google.",
    sub: "Eventi, ricorrenze e promemoria automatici. Sincronizzazione in tempo reale con il team.",
    icon: CalendarDays,
    bullets: ["Promemoria automatici push + email", "Ricorrenze giornaliere · settimanali · mensili", "Sync Google Calendar pronta"],
    bg: "bg-[#0A0A0A]",
    fg: "text-white",
    accentText: "text-[#FF7A29]",
    visual: "calendar",
    image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=1920&q=60",
    overlay: "bg-[#0A0A0A]/80",
  },
  {
    id: "qr",
    kicker: "02 · Presenze QR + GPS",
    title: "Timbrature geolocalizzate.",
    sub: "QR personalizzati per sede con validazione GPS. Niente più badge smarriti o presenze fittizie.",
    icon: QrCode,
    bullets: ["Raggio configurabile · validazione Haversine", "QR stampabile per ogni sede", "Timbratura da smartphone in 2 tap"],
    bg: "bg-[#FF7A29]",
    fg: "text-white",
    accentText: "text-[#0A0A0A]",
    visual: "qr",
    image: "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?auto=format&fit=crop&w=1920&q=60",
    overlay: "bg-[#FF7A29]/85",
  },
  {
    id: "expenses",
    kicker: "03 · Rimborsi Spese",
    title: "Note spese in un tap.",
    sub: "I dipendenti caricano lo scontrino, HR approva in un click, esportazione diretta in paghe.",
    icon: Receipt,
    bullets: ["Upload foto scontrino istantaneo", "Workflow di approvazione configurabile", "Categorie Trasporti · Pasti · Alloggio …"],
    bg: "bg-[#FFFAF5]",
    fg: "text-[#0A0A0A]",
    accentText: "text-[#FF7A29]",
    visual: "expenses",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1920&q=60",
    overlay: "bg-[#FFFAF5]/88",
  },
  {
    id: "signature",
    kicker: "04 · Firma Digitale",
    title: "Firme con valore legale.",
    sub: "Canvas touch/mouse + hash crittografico SHA-256. Documenti sigillati, manomissioni impossibili.",
    icon: FileSignature,
    bullets: ["Firma da desktop, tablet, smartphone", "Hash SHA-256 per ogni firma", "Registro immutabile delle firme"],
    bg: "bg-[#0A0A0A]",
    fg: "text-white",
    accentText: "text-[#FF7A29]",
    visual: "signature",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1920&q=60",
    overlay: "bg-[#0A0A0A]/82",
  },
  {
    id: "payroll",
    kicker: "05 · Export Paghe",
    title: "Direttamente al consulente paghe.",
    sub: "CSV standard compatibile con Zucchetti, TeamSystem e Inaz. Nessuna riformattazione.",
    icon: FileSpreadsheet,
    bullets: ["Compatibilità certificata Zucchetti", "TeamSystem · Inaz · formati custom", "Aggregazione automatica ore lavorate"],
    bg: "bg-[#FFFAF5]",
    fg: "text-[#0A0A0A]",
    accentText: "text-[#FF7A29]",
    visual: "payroll",
    image: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?auto=format&fit=crop&w=1920&q=60",
    overlay: "bg-[#FFFAF5]/90",
  },
  {
    id: "ai",
    kicker: "06 · AI Analisi Presenze",
    title: "GPT-5.2 lavora per te.",
    sub: "L'intelligenza artificiale rileva anomalie, riconosce pattern e suggerisce azioni in italiano.",
    icon: Brain,
    bullets: ["Analisi di periodo (7 / 30 / 90 giorni)", "Report in italiano · formato leggibile", "Anomalie GPS e straordinari rilevati"],
    bg: "bg-[#FF7A29]",
    fg: "text-white",
    accentText: "text-[#0A0A0A]",
    visual: "ai",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1920&q=60",
    overlay: "bg-[#FF7A29]/82",
  },
];

function Visual({ kind }) {
  if (kind === "calendar") {
    // Calendario sempre corretto basato sulla data corrente
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-11
    const monthLabelRaw = today.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    const monthLabel = monthLabelRaw.charAt(0).toUpperCase() + monthLabelRaw.slice(1);
    const lastDate = new Date(year, month + 1, 0).getDate(); // 28/29/30/31 corretto
    const firstDayWeek = new Date(year, month, 1).getDay(); // 0=Dom..6=Sab
    const offset = (firstDayWeek + 6) % 7; // lunedì-first
    const totalCells = Math.ceil((offset + lastDate) / 7) * 7;
    const cells = Array.from({ length: totalCells }, (_, i) => {
      const d = i - offset + 1;
      return d >= 1 && d <= lastDate ? d : null;
    });
    // Giorni "occupati" sempre dentro il mese
    const busy = [3, 7, 12, 15, 18, 22, 25, 28].filter((d) => d <= lastDate);
    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl">{monthLabel}</div>
          <div className="flex gap-1"><ChevronLeft className="w-4 h-4"/><ChevronRight className="w-4 h-4"/></div>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => (<div key={`h-${i}`} className="text-[10px] uppercase tracking-widest opacity-50 text-center py-1">{d}</div>))}
          {cells.map((d, i) => (
            <div key={i} className={`aspect-square flex items-center justify-center text-xs rounded-lg ${d === null ? "opacity-0" : busy.includes(d) ? "bg-[#FF7A29] font-semibold text-white" : "bg-white/10"}`}>
              {d || ""}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (kind === "qr") {
    return (
      <div className="bg-white rounded-3xl p-8 shadow-2xl text-[#0A0A0A]">
        <div className="aspect-square bg-white p-3 border-4 border-[#0A0A0A] rounded-xl max-w-xs mx-auto">
          <svg viewBox="0 0 21 21" width="100%" height="100%" shapeRendering="crispEdges">
            {[
              "11111110101011111111","10000010111010000001","10111010100010111011","10111010111010111011","10111010010110111010",
              "10000010101010000001","11111110101011111111","00000000100000000000","10101111011101110101","11011010111011010111",
              "00100110010100101101","11011110101110110111","01110111010011010001","00000000111100001101","11111110101000111011",
              "10000010001011001001","10111010101110111110","10111010110111100011","10111010111110001111","10000010010010110001","11111110101011001011",
            ].map((row, y) => row.split("").map((bit, x) => bit === "1" ? <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#000"/> : null))}
          </svg>
        </div>
        <div className="mt-6 text-center">
          <div className="font-display text-xl">Sede Milano</div>
          <div className="text-xs text-[#757575]">45.4641°N · 9.1900°E · raggio 100m</div>
        </div>
      </div>
    );
  }
  if (kind === "expenses") {
    const rows = [
      { d: "02/02", w: "Pranzo cliente", a: "47,50", s: "ok" },
      { d: "03/02", w: "Treno AV Milano-Roma", a: "124,00", s: "pending" },
      { d: "04/02", w: "Hotel Excelsior", a: "189,00", s: "pending" },
      { d: "05/02", w: "Carburante", a: "65,00", s: "ok" },
    ];
    return (
      <div className="bg-white rounded-3xl p-6 shadow-xl border border-[#EDE5D8]">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg">Richieste di rimborso</div>
          <span className="text-xs bg-[#FF7A29] text-white px-3 py-1 rounded-full">4 pendenti</span>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#FFFAF5] border border-[#EDE5D8]">
              <div>
                <div className="text-sm font-semibold">{r.w}</div>
                <div className="text-[10px] text-[#757575]">{r.d}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-display text-lg">€{r.a}</span>
                <span className={`text-[10px] px-2 py-1 rounded-full ${r.s === "ok" ? "bg-[#FF7A29] text-white" : "bg-[#0A0A0A] text-white"}`}>{r.s === "ok" ? "Approvato" : "Attesa"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (kind === "signature") {
    return (
      <div className="bg-white rounded-3xl p-8 shadow-2xl text-[#0A0A0A]">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-4">Contratto di assunzione — Pag. 12/12</div>
        <div className="border-2 border-dashed border-[#EDE5D8] rounded-xl p-6 bg-[#FFFAF5]">
          <svg viewBox="0 0 300 80" className="w-full h-20">
            <path d="M 10 50 Q 30 10 50 45 T 90 50 Q 110 20 130 50 T 180 55 Q 210 30 240 60 T 290 50" fill="none" stroke="#0A0A0A" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <div className="text-[10px] text-[#757575] text-right">M. De Angelis · 05.02.2026 · 14:22</div>
        </div>
        <div className="mt-4 bg-[#FFFAF5] rounded-lg p-3 font-mono text-[9px] break-all text-[#555] border border-[#EDE5D8]">
          SHA-256: 8f4e2a9c7b1d6e3f5a8b2c9d4e7f1a6b3c8d5e9f2a7b4c1d6e3f8a5b2c9d4e7f
        </div>
        <div className="mt-3 text-xs text-[#FF7A29] font-semibold flex items-center gap-1">✓ Firma verificata e sigillata</div>
      </div>
    );
  }
  if (kind === "payroll") {
    return (
      <div className="bg-[#0A0A0A] text-[#FF7A29] rounded-3xl p-6 font-mono text-sm shadow-2xl">
        <div className="flex items-center justify-between mb-4 text-white/60 text-xs">
          <span>paghe_202602.csv</span>
          <span className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]"/>
            <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]"/>
            <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]"/>
          </span>
        </div>
        <pre className="leading-6 overflow-hidden whitespace-pre text-[11px] md:text-[13px]">{`MATRICOLA;NOMINATIVO;DATA;ENTRATA;USCITA;ORE
E0001;ROSSI MARIO;2026-02-03;09:02;18:15;9.21
E0002;VERDI LUCIA;2026-02-03;08:45;17:30;8.75
E0003;NERI PAOLO;2026-02-03;09:15;18:00;8.75
E0001;ROSSI MARIO;2026-02-04;08:58;18:05;9.11
E0004;BIANCHI S.;2026-02-04;09:00;17:45;8.75
E0005;ESPOSITO F.;2026-02-04;08:50;18:20;9.50`}</pre>
        <div className="mt-4 pt-3 border-t border-white/10 text-xs text-white/50">✓ Pronto per Zucchetti · TeamSystem · Inaz</div>
      </div>
    );
  }
  if (kind === "ai") {
    return (
      <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl text-[#0A0A0A]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#0A0A0A] text-[#FF7A29] flex items-center justify-center">
            <Brain className="w-5 h-5"/>
          </div>
          <div>
            <div className="font-display text-lg leading-none">Report AI</div>
            <div className="text-[10px] text-[#757575]">GPT-5.2 · 2.4s</div>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#FF7A29] font-semibold mb-1">Riepilogo</div>
        <p className="text-sm leading-relaxed text-[#222] mb-4">Rilevati 3 pattern ricorrenti di entrate tardive il lunedì mattina (+12 min). Team Logistica +8% efficienza.</p>
        <div className="text-[10px] uppercase tracking-widest text-[#FF7A29] font-semibold mb-1">Anomalie</div>
        <ul className="text-xs space-y-1 text-[#444]">
          <li>— 2 timbrature fuori raggio Milano</li>
          <li>— Mancate uscite: 1 dipendente ricorrente</li>
          <li>— Straordinari non autorizzati: 5h</li>
        </ul>
      </div>
    );
  }
  return null;
}

function HeroSlide({ slide, onDemo, parallaxY, kbClass }) {
  const Icon = slide.icon;
  const isLight = slide.bg === "bg-[#FFFAF5]";
  return (
    <section className={`${slide.bg} ${slide.fg} relative overflow-hidden min-h-[680px] md:min-h-[740px] flex items-center`}>
      {/* Background image with Ken Burns */}
      {slide.image && (
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img src={slide.image} alt="" className={`w-full h-full object-cover ${kbClass || "kenburns-a"}`} loading="eager" />
          <div className={`absolute inset-0 ${slide.overlay || "bg-[#0A0A0A]/80"}`}/>
          <div className={`absolute inset-0 ${isLight ? "bg-gradient-to-r from-[#FFFAF5] via-[#FFFAF5]/70 to-transparent" : slide.bg === "bg-[#FF7A29]" ? "bg-gradient-to-r from-[#FF7A29]/95 via-[#FF7A29]/40 to-transparent" : "bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/50 to-transparent"}`}/>
        </div>
      )}
      <div className={`orb ${slide.bg === "bg-[#0A0A0A]" ? "orb-orange" : "orb-black"} z-0`} style={{width: 600, height: 600, top: -200, right: -150, opacity: slide.bg === "bg-[#FF7A29]" ? 0.18 : 0.3}}/>
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-14 py-16 grid md:grid-cols-12 gap-10 items-center w-full">
        <div className="md:col-span-7">
          <div className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] ${slide.accentText} mb-6 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full`}>
            <Icon className="w-4 h-4"/> {slide.kicker}
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter drop-shadow-sm">
            {slide.title}
          </h1>
          <p className={`mt-6 text-base md:text-lg max-w-xl leading-relaxed ${isLight ? "text-[#333]" : "opacity-90"}`}>
            {slide.sub}
          </p>
          <ul className="mt-8 space-y-2.5">
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className={`mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full ${slide.accentText.replace("text-", "bg-")}`}/>
                <span className={isLight ? "text-[#333]" : "opacity-85"}>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register">
              <Button className={`rounded-full h-12 px-7 text-sm font-semibold ${slide.bg === "bg-[#FF7A29]" ? "bg-[#0A0A0A] text-white hover:bg-white hover:text-[#0A0A0A]" : "bg-[#FF7A29] text-white hover:bg-[#E85D00] btn-glow shadow-lg shadow-orange-400/40"}`}>
                Prova gratis 14 giorni <ArrowUpRight className="ml-1 w-4 h-4"/>
              </Button>
            </Link>
            <Button onClick={onDemo} variant="outline" className={`rounded-full h-12 px-7 text-sm font-semibold ${isLight ? "border-[#0A0A0A] bg-white/70 backdrop-blur text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" : "border-white/40 bg-white/10 backdrop-blur text-current hover:bg-white hover:text-[#0A0A0A]"}`}>
              Richiedi demo
            </Button>
          </div>
        </div>
        <div className="md:col-span-5">
          <div
            className="relative float will-change-transform"
            style={{ transform: `translateY(${parallaxY}px)` }}
          >
            <Visual kind={slide.visual}/>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HeroCarousel({ onDemo }) {
  const [api, setApi] = useState(null);
  const [idx, setIdx] = useState(0);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    if (!api) return;
    setIdx(api.selectedScrollSnap());
    api.on("select", () => setIdx(api.selectedScrollSnap()));
    const t = setInterval(() => api.scrollNext(), 7000);
    return () => clearInterval(t);
  }, [api]);

  // Parallax: shift right-side visual based on scroll position
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        // Clamp parallax to max 60px when scrolled within first 800px
        const shift = Math.min(60, y * 0.12);
        setParallaxY(shift);
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const kbVariants = ["kenburns-a", "kenburns-b", "kenburns-c"];

  return (
    <div className="relative" data-testid="hero-carousel">
      {/* Story-style progress bars at top */}
      <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 px-4 pt-2" data-testid="hero-progress">
        {SLIDES.map((_, i) => (
          <div key={i} className="flex-1 h-1 bg-white/15 rounded-full overflow-hidden">
            {i === idx && (
              <div key={`fill-${idx}`} className="h-full bg-[#FF7A29] progress-fill" />
            )}
            {i < idx && <div className="h-full w-full bg-[#FF7A29]/60"/>}
          </div>
        ))}
      </div>

      <Carousel setApi={setApi} opts={{ loop: true, align: "start" }}>
        <CarouselContent>
          {SLIDES.map((s, i) => (
            <CarouselItem key={s.id} className="basis-full">
              <HeroSlide slide={s} onDemo={onDemo} parallaxY={parallaxY} kbClass={kbVariants[i % kbVariants.length]}/>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Controls overlay */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/80 backdrop-blur-md rounded-full px-3 py-2 shadow-lg border border-black/5 z-10">
        <button
          onClick={() => api?.scrollPrev()}
          aria-label="Precedente"
          data-testid="hero-carousel-prev"
          className="w-8 h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center hover:bg-[#FF7A29] transition-colors"
        >
          <ChevronLeft className="w-4 h-4"/>
        </button>
        <div className="flex items-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              aria-label={`Vai a ${s.id}`}
              data-testid={`hero-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-[#FF7A29]" : "w-4 bg-[#D4D4D4] hover:bg-[#0A0A0A]"}`}
            />
          ))}
        </div>
        <button
          onClick={() => api?.scrollNext()}
          aria-label="Successivo"
          data-testid="hero-carousel-next"
          className="w-8 h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center hover:bg-[#FF7A29] transition-colors"
        >
          <ChevronRight className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}
